/**
 * Présence réelle de l'utilisateur : dernière interaction (toucher, clic, clavier,
 * défilement) alors que l'application est au premier plan.
 *
 * Sert à distinguer une personne qui utilise l'application d'un onglet laissé ouvert
 * qui continue d'appeler le serveur (géolocalisation, sonde de connectivité,
 * rafraîchissements) : seules les requêtes accompagnées d'une présence récente font
 * avancer la « dernière connexion » et le statut en ligne (AppRequestInterceptor,
 * en-tête x-user-idle posé par axiosService sur les appels à notre API).
 */

// Au-delà, l'utilisateur n'est plus considéré présent même si l'onglet est visible
const PRESENCE_WINDOW = 5 * 60 * 1000;
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'];

class AppPresenceService {

  constructor() {
    // Au chargement, l'application vient d'être ouverte : l'utilisateur est là
    this.lastInteraction = Date.now();
    this.listening = false;
  }

  /** Écoute des interactions ; appelé une fois au démarrage (sans effet côté serveur) */
  start() {
    if (this.listening || typeof window === 'undefined') return;
    this.listening = true;
    const touch = () => { this.lastInteraction = Date.now(); };
    INTERACTION_EVENTS.forEach((name) => window.addEventListener(name, touch, {passive: true, capture: true}));
    // Retour au premier plan (onglet ou PWA rouverte) : c'est une action de l'utilisateur
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') touch();
    });
  }

  /** Application au premier plan (onglet visible) */
  isVisible() {
    return typeof document === 'undefined' || document.visibilityState !== 'hidden';
  }

  /**
   * Secondes écoulées depuis la dernière interaction, ou null si l'utilisateur
   * n'est pas présent (application en arrière-plan, ou inactivité prolongée)
   */
  idleSeconds() {
    if (!this.isVisible()) return null;
    const idle = Date.now() - this.lastInteraction;
    if (idle > PRESENCE_WINDOW) return null;
    return Math.max(0, Math.round(idle / 1000));
  }
}

export default new AppPresenceService();
